/**
 * --VALIDATION FORM--
 *
 * children: An array of React input components used to collect data for the form.
 * data: The object that stores the data for the form. Can be nested at any depth.
 *    Is often the variable of a useState/useReducer.
 * setData: A function used to update the data object. Is often the function of a useState/useReducer.
 * setError: A function used to communicate errors to the parent component.
 *    Is often the function of a useState/useReducer.
 */

import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import {
  KakarikoDates,
  KakarikoEditingCell,
  KakarikoRadio,
  KakarikoSelect,
  KakarikoText,
  Validations,
} from '../Inputs';
import {
  flatStateReducer,
  nestedStateReducer,
  retrieveNestedValue,
  setNestedValue,
} from '../utils';
import { StyledKakarikoForm } from './styles';
import type { IKakarikoFormProps, ReactChildOrChildren } from './types';

const isInput = (element: React.ReactElement) => {
  if (typeof element.type === 'string') return false;
  const inputTypes = [
    KakarikoSelect.name,
    KakarikoText.name,
    KakarikoEditingCell.name,
    KakarikoRadio.name,
  ];
  return inputTypes.includes(element.type.name);
};

const isKakarikoDates = (element: React.ReactElement) => {
  if (typeof element.type === 'string') return false;
  return element.type.name === KakarikoDates.name;
};

export const KakarikoForm = (props: IKakarikoFormProps) => {
  const { children, data, id, isEditing, setData, setError } = props;

  const [internalData, setInternalData] = useReducer<genericReducerObject>(
    nestedStateReducer,
    data,
  );
  const [internalErrors, setInternalErrors] = useReducer<
    React.Reducer<booleanGenericObject, booleanGenericObject>
  >(flatStateReducer, {});
  const validations = useRef<{ [key: string]: any }>({});

  useEffect(() => {
    if (setData) setData(internalData);
  }, [internalData, setData]);

  useEffect(() => {
    if (setError) setError(internalErrors);
  }, [internalErrors, setError]);

  const updateProperty = useCallback(
    (fieldName: string | string[], value: any) => {
      // Allows for changes in nested object structure.
      setInternalData({ ...setNestedValue(fieldName, internalData, value) });
    },
    [internalData, setInternalData],
  );

  const retrieveValue = useCallback(
    (name: string | string[]) => {
      return retrieveNestedValue(name, internalData);
    },
    [internalData],
  );

  // Adds default props into all children of the Form to ensure default behavior.
  const recursiveMap = useCallback(
    (children: ReactChildOrChildren): ReactChildOrChildren => {
      return React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
          return child;
        }

        let clone;
        if (child.props.children) {
          clone = React.cloneElement(child, {
            children: recursiveMap(child.props.children),
          });
        } else if (isInput(child) || isKakarikoDates(child)) {
          const label = child.props?.label;
          const name: string = child.props?.name;
          const names: string[] = child.props?.names;
          if (name && names) {
            throw new Error(
              "Form Input fields should only use 'name' or 'names' props.",
            );
          }
          let hasError =
            child.props?.validation &&
            (child.props?.hasError ||
              internalErrors[name] ||
              names
                ?.map((name: string) => ({ [name]: internalErrors?.[name] }))
                .reduce((acc: any, curr: any) => ({ ...acc, ...curr })));
          let handleBlur =
            child.props.handleBlur ||
            ((e: any) => updateProperty(name ?? names, e));
          let handleChange =
            child.props.handleChange ||
            ((e: any) => updateProperty(name ?? names, e));

          if (child.props.validation) {
            // Nested Array for multiple values.
            if (names && child.props.validation) {
              if (
                !Array.isArray(child.props.validation[0]) &&
                names.length !== child.props.validation
              ) {
                throw new Error(
                  "Validation must be a 2 dimensional Array of the same size as the 'names' array.",
                );
              }
              names.forEach((name: string, index: number) => {
                validations.current = {
                  ...validations.current,
                  [name]: child.props.validation[index],
                };
              });
            }
            validations.current = {
              ...validations.current,
              [name]: child.props.validation,
            };
          }

          if (isKakarikoDates(child)) {
            validations.current = {
              ...validations.current,
              [`${name}.year`]: [
                Validations.isExactLength(4),
                Validations.isValidBirthYear,
              ],
              [`${name}.month`]: [
                Validations.isUnderMaxValue(13),
                Validations.isPositiveNumber,
              ],
              [`${name}.day`]: [
                Validations.isUnderMaxValue(32),
                Validations.isPositiveNumber,
              ],
            };
            hasError = {
              year: internalErrors[`${name}.year`],
              month: internalErrors[`${name}.month`],
              day: internalErrors[`${name}.day`],
            };
          }

          clone = React.cloneElement(child, {
            handleBlur,
            handleChange,
            hasError: hasError,
            ...(isEditing !== undefined && { isEditing }),
            errorMessage:
              child.props.errorMessage ||
              `Please enter a valid ${label || name}.`,
            value: child.props.value || retrieveValue(name ?? names),
            variant: child.props.variant || 'filled',
          });
        }

        return clone;
      });
    },
    [internalErrors, isEditing, retrieveValue, updateProperty],
  );

  // Run validations on each field whenever the data changes.
  useEffect(() => {
    Object.entries(validations.current).forEach(([field, fns]) => {
      if (!Array.isArray(fns)) return;
      const valid = fns.reduce((valid, fn) => {
        const v = fn(retrieveValue(field));
        return v === true && valid === true;
      }, true);
      setInternalErrors({ [field]: !valid });
    });
  }, [internalData, retrieveValue]);

  const elements = useCallback(() => {
    return recursiveMap(children);
  }, [children, recursiveMap]);

  return <StyledKakarikoForm id={id || ''}>{elements()}</StyledKakarikoForm>;
};
